import { useQuery } from '@tanstack/react-query';

import {
  EarningResponse,
  FetchEarningsByPeriodRequest,
  earningsService,
  getError,
  useToast,
} from '@aslanpay/portal-common-lib';

// A pay cycle that wallet-manager cannot resolve is an expected, self-healing state rather than a
// failure worth interrupting the admin over: wallet-manager resolves the cycle against its own
// mirror of the individual, which syncs asynchronously from CM_INDIVIDUAL_UPDATED. Straight after a
// pay-calendar reassignment the page already holds the new cycle ids (calendar-manager is
// authoritative and immediate) while the mirror still holds the old calendar, so the lookup 404s
// for about a second. Retry once so the earnings actually load once the mirror catches up, and keep
// the toast for every other failure.
const isPayCycleNotResolved = (error: Error) => getError(error).status === 404;

const useFetchEarnings = (
  { individualId, payCycleId, sort }: FetchEarningsByPeriodRequest,
  enabled = true
) => {
  const toast = useToast();

  return useQuery<EarningResponse[], Error>(
    ['fetchEarnings', individualId, payCycleId, sort],
    () => earningsService.fetchEarningsByPeriod({ individualId, payCycleId, sort }),
    {
      enabled,
      retry: (failureCount, error) => isPayCycleNotResolved(error) && failureCount < 1,
      retryDelay: 1500,
      onError: (error) => {
        if (isPayCycleNotResolved(error)) {
          return;
        }

        toast({
          position: 'top-right',
          status: 'error',
          title: 'Something went wrong',
          description: `Sorry, we failed to fetch the earnings. Please try again.`,
        });
      },
    }
  );
};

export default useFetchEarnings;
